import { trackLongSession5Min } from "@/lib/analytics/gtm-datalayer";
import { getLanguageNameByCode } from "@/lib/livekit-agent-telemetry";
import {
  GUEST_USAGE_TODAY_KEY,
  getDateScopedUsageSeconds,
  getGuestTotalUsageSeconds,
  getRegisteredUsageSeconds,
  incrementGuestUsageSeconds,
  setRegisteredUsageSeconds,
} from "@/lib/livekit-agent-usage-storage";
import type { LiveKitSessionUsage } from "@/lib/livekit-session-usage";
import { DEFAULT_SESSION_MODE, type SessionMode } from "@/lib/sessionRouting";
import { useCallback, useEffect, useRef } from "react";

type RecordingTimerLogger = {
  info: (message: string, data?: unknown) => void;
  warn: (message: string, data?: unknown) => void;
  error: (message: string, data?: unknown) => void;
};

type PostHogLike = {
  capture: (eventName: string, properties?: Record<string, unknown>) => void;
};

type RecordingTimerUser = {
  email?: string | null;
};

type MutableRef<T> = {
  current: T;
};

type UseLiveKitRecordingTimerInput = {
  // Identifies one connection (the room). Effect restarts, re-renders and
  // reconnects to the same room keep its clock; a new room starts a new one.
  sessionKey?: string | null;
  isRecording: boolean;
  user?: RecordingTimerUser | null;
  authenticatedSessionUsage: LiveKitSessionUsage | null;
  hasExceededGuestLimit: boolean;
  selectedLanguageCode: string;
  selectedLanguageLevel: string;
  sessionMode?: SessionMode;
  analyticsSurface: string;
  posthogClient?: PostHogLike | null;
  maxGuestSeconds: number;
  hasTrackedEngagedSessionRef: MutableRef<boolean>;
  hasTrackedGuestLimitReachedRef: MutableRef<boolean>;
  setRecordingTime: React.Dispatch<React.SetStateAction<number>>;
  setHasExceededGuestLimit: (value: boolean) => void;
  disconnectFromLiveKit: (
    triggeredByTimer?: boolean,
    invalidateTokenCache?: boolean,
    source?: string,
    sessionDurationSeconds?: number
  ) => void;
  logger: RecordingTimerLogger;
};

type TimerState = {
  key?: string | null;
  startMonotonicMs: number;
  counted: number;
  ended: boolean;
};

export function useLiveKitRecordingTimer(input: UseLiveKitRecordingTimerInput) {
  const latest = useRef(input);
  latest.current = input;
  const state = useRef<TimerState | null>(null);
  const { isRecording, sessionKey, setRecordingTime } = input;

  const update = useCallback(() => {
    const p = latest.current;
    if (!p.isRecording || (!p.user && p.hasExceededGuestLimit)) return;
    if (!state.current || state.current.key !== p.sessionKey) {
      state.current = {
        key: p.sessionKey,
        startMonotonicMs: performance.now(),
        counted: 0,
        ended: false,
      };
    }
    const s = state.current;
    if (s.ended) return;

    // Elapsed time comes from the monotonic clock, so throttled or delayed
    // callbacks catch up and device clock changes never count as usage.
    const connectedSeconds = Math.max(0, (performance.now() - s.startMonotonicMs) / 1000);
    const freemiumGrant =
      p.user && p.authenticatedSessionUsage?.policy === "freemium"
        ? p.authenticatedSessionUsage.sessionLimitSeconds
        : Infinity;
    const guestRemaining = p.user
      ? Infinity
      : Math.max(0, p.maxGuestSeconds - getGuestTotalUsageSeconds());
    const elapsed = Math.min(
      freemiumGrant,
      s.counted + guestRemaining,
      Math.max(s.counted, Math.floor(connectedSeconds))
    );
    const delta = elapsed - s.counted;
    s.counted = elapsed;
    p.setRecordingTime(elapsed);

    // Attribute new usage to the day the device clock shows now. Usage capped
    // by a limit ended when the limit was reached, not at this callback.
    const now = Date.now();
    const midnight = new Date(now);
    midnight.setHours(0, 0, 0, 0);
    const usageEnd = now - Math.max(0, connectedSeconds - elapsed) * 1000;
    const todayDelta = Math.min(
      delta,
      Math.max(0, Math.floor((usageEnd - midnight.getTime()) / 1000))
    );

    if (!p.user) {
      const totalUsage = delta
        ? incrementGuestUsageSeconds(p.logger, delta, todayDelta)
        : getGuestTotalUsageSeconds();
      if (totalUsage < p.maxGuestSeconds) return;
      s.ended = true;
      if (p.maxGuestSeconds >= 300 && !p.hasTrackedEngagedSessionRef.current) {
        p.hasTrackedEngagedSessionRef.current = true;
        trackLongSession5Min({
          durationMinutes: Number((p.maxGuestSeconds / 60).toFixed(2)),
          sessionMode: p.sessionMode || DEFAULT_SESSION_MODE,
          userType: "guest",
          selectedLanguageCode: p.selectedLanguageCode,
          selectedLanguageName: getLanguageNameByCode(p.selectedLanguageCode),
        });
        p.logger.info("Long session GA4 event tracked at guest demo completion", {
          guestSessionLimitSeconds: p.maxGuestSeconds,
        });
      }
      if (
        p.analyticsSurface === "fullscreen" &&
        p.posthogClient &&
        !p.hasTrackedGuestLimitReachedRef.current
      ) {
        p.hasTrackedGuestLimitReachedRef.current = true;
        p.posthogClient.capture("fullscreen_guest_demo_limit_reached", {
          selected_language_code: p.selectedLanguageCode,
          selected_language_name: getLanguageNameByCode(p.selectedLanguageCode),
          selected_language_level: p.selectedLanguageLevel,
          guest_seconds_used_total: totalUsage,
          guest_seconds_used_today: getDateScopedUsageSeconds(GUEST_USAGE_TODAY_KEY, p.logger),
        });
      }
      // The disconnect closure still holds the previous render's recordingTime.
      p.disconnectFromLiveKit(true, false, "guest_limit_timer", elapsed);
      p.setHasExceededGuestLimit(true);
      return;
    }

    if (Number.isFinite(freemiumGrant) && delta && p.user.email) {
      setRegisteredUsageSeconds(
        p.user.email,
        getRegisteredUsageSeconds(p.user.email, p.logger) + todayDelta
      );
    }
    if (elapsed >= 300 && p.sessionMode !== "maths" && !p.hasTrackedEngagedSessionRef.current) {
      p.hasTrackedEngagedSessionRef.current = true;
      trackLongSession5Min({
        durationMinutes: 5,
        sessionMode: p.sessionMode || DEFAULT_SESSION_MODE,
        userType: "authenticated",
        selectedLanguageCode: p.selectedLanguageCode,
        selectedLanguageName: getLanguageNameByCode(p.selectedLanguageCode),
      });
      p.logger.info("Long session GA4 event tracked (5 min)");
    }
    if (elapsed >= freemiumGrant) {
      s.ended = true;
      if (p.sessionMode !== "maths") {
        p.disconnectFromLiveKit(true, false, "server_freemium_grant_timer", elapsed);
      }
    }
  }, []);

  useEffect(() => {
    if (!isRecording) {
      if (!sessionKey) state.current = null;
      setRecordingTime(0);
      return;
    }
    update();
    const interval = setInterval(update, 1000);
    const resume = () => {
      if (document.visibilityState !== "hidden") update();
    };
    window.addEventListener("focus", resume);
    document.addEventListener("visibilitychange", resume);
    return () => {
      clearInterval(interval);
      window.removeEventListener("focus", resume);
      document.removeEventListener("visibilitychange", resume);
    };
  }, [isRecording, sessionKey, update, setRecordingTime]);
}
